import { Alert, Avatar, Badge, Button, Group, Input, NumberInput, SimpleGrid, Stack, Tabs, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { EmailVerificationCard } from "../components/EmailVerificationCard";
import { PhoneVerificationCard } from "../components/PhoneVerificationCard";
import { NotificationPreferencesCard } from "../components/NotificationPreferencesCard";
import { RecruiterTermsCard } from "../components/RecruiterTermsCard";
import { ResumeLink } from "../components/ResumeLink";
import { ShareIntakeLinkButton } from "../components/ShareIntakeLinkButton";
import { ContentCard, ErrorState, LoadingOverlay, PageHeader, StatusBadge } from "../components/ui";
import { CitySelect } from "../components/CitySelect";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "../context/useAuth";
import { isIdentityVerified } from "../lib/accountVerification";
import { recruiterApi, intakeApi } from "../lib/api";
import { DeleteAccountCard } from "../components/profile/DeleteAccountCard";
import { ApiError } from "../lib/apiClient";
import { TERMS_VERSION } from "../constants/platformTerms";
import { formatDate, formatExperience, SEGMENT_LABELS } from "../lib/format";
import { SEGMENT_SELECT_COPY } from "../lib/segments";
import { kycStatusLabel, recruiterProfileEditable } from "../lib/kyc";
import { axioTokens } from "../theme";

const DOC_SLOTS: { key: string; label: string }[] = [
  { key: "selfie", label: "Live photo" },
  { key: "aadhaar_front", label: "Aadhaar front" },
  { key: "aadhaar_back", label: "Aadhaar back" },
  { key: "pan_front", label: "PAN front" },
  { key: "pan_back", label: "PAN back" },
];

function docStatusLabel(filled: Set<string>, key: string, approved: boolean): string {
  if (!filled.has(key)) return "Not uploaded";
  return approved ? "Verified" : "Uploaded";
}

export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const profileQuery = useQuery({ queryKey: ["my-profile"], queryFn: recruiterApi.myProfile });
  const kycQuery = useQuery({ queryKey: ["my-kyc"], queryFn: recruiterApi.kycStatus });
  const intakeLinksQuery = useQuery({ queryKey: ["my-intake-links"], queryFn: intakeApi.listLinks });

  const form = useForm({
    initialValues: { city: "", years_experience: undefined as number | undefined, bio: "" },
  });

  useEffect(() => {
    if (profileQuery.data) {
      form.setValues({
        city: profileQuery.data.city ?? "",
        years_experience: profileQuery.data.years_experience ?? undefined,
        bio: profileQuery.data.bio ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileQuery.data]);

  async function handleSubmit(values: typeof form.values) {
    setSubmitting(true);
    try {
      await recruiterApi.updateMyProfile({
        city: values.city || undefined,
        years_experience: values.years_experience,
        bio: values.bio || undefined,
      });
      notifications.show({ color: "green", title: "Saved", message: "Your profile has been updated." });
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Could not save", message });
    } finally {
      setSubmitting(false);
    }
  }

  if (profileQuery.isLoading || kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }
  if (profileQuery.isError || kycQuery.isError) {
    return <ErrorState message="Could not load your profile. Please refresh and try again." />;
  }

  const profile = profileQuery.data;
  const kyc = kycQuery.data;
  const activeIntakeLink = (intakeLinksQuery.data ?? []).find((link) => link.is_active);
  const editable = recruiterProfileEditable(profile);
  const filled = new Set(kyc?.filled_slots ?? []);
  const docsComplete = kyc?.kyc_documents_complete ?? profile?.kyc_documents_complete ?? false;
  const termsVersion = user?.terms_version ?? TERMS_VERSION;
  const termsAcceptedAt = user?.terms_accepted_at;

  const approved = docsComplete && profile?.kyc_status === "verified";
  const initials = (profile?.user.full_name ?? "Recruiter")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const segmentValues = profile?.segments ?? [];
  const segmentsField = (
    <Input.Wrapper label={SEGMENT_SELECT_COPY.recruitForLabel} labelProps={{ mb: "sm" }}>
      {segmentValues.length === 0 ? (
        <Text size="sm" c="dimmed">{SEGMENT_SELECT_COPY.empty}</Text>
      ) : (
        <Group gap="sm" wrap="wrap">
          {segmentValues.map((segment) => (
            <Badge
              key={segment}
              variant="light"
              c="var(--axio-color-muted)"
              bg="var(--axio-color-media-background)"
            >
              {SEGMENT_LABELS[segment] ?? segment}
            </Badge>
          ))}
        </Group>
      )}
    </Input.Wrapper>
  );

  return (
    <Stack gap="lg" maw="var(--axio-unit-960)" mx="auto">
      <PageHeader title="My profile" subtitle="Manage your account, verification, compliance, and recruiter tools." />

      <ContentCard style={{ background: axioTokens.profileHeader }}>
        <Group align="flex-start" wrap="nowrap">
          <Avatar size="var(--axio-unit-72)" radius={axioTokens.radiusCircle} color="violet" variant="light">
            {initials}
          </Avatar>
          <Stack gap="xs" style={{ flex: "var(--axio-number-one)", minWidth: "var(--axio-unit-0)" }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <div>
                <Title order={3}>{profile?.user.full_name}</Title>
                <Text size="sm" c="dimmed">{profile?.user.email} · {profile?.user.phone || "Phone not added"}</Text>
              </div>
              <Group gap="md">
                <StatusBadge status={profile?.kyc_status ?? "pending"} />
                <Badge color="gray" variant="outline">Tier {profile?.tier}</Badge>
              </Group>
            </Group>
            <Text size="xs" c="dimmed">
              Member since {formatDate(profile?.created_at)}
              {profile?.kyc_submitted_at ? ` · KYC submitted ${formatDate(profile.kyc_submitted_at)}` : ""}
            </Text>
          </Stack>
        </Group>
      </ContentCard>

      {profile?.consultancy_name && (
        <Alert color="violet" title={`Affiliated with ${profile.consultancy_name}`}>
          Placement payouts go to your consultancy.
        </Alert>
      )}

      <Tabs defaultValue="account" variant="outline">
        <Tabs.List grow>
          <Tabs.Tab value="account">Account</Tabs.Tab>
          <Tabs.Tab value="verification">Verification</Tabs.Tab>
          <Tabs.Tab value="compliance">Compliance</Tabs.Tab>
          <Tabs.Tab value="preferences">Preferences</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="account" pt="lg">
          <ContentCard>
            <Title order={4} mb="md">Recruiter details</Title>
            {editable ? (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack>
                  <TextInput label="Full name" value={profile?.user.full_name ?? ""} disabled />
                  <TextInput label="Email" value={profile?.user.email ?? ""} disabled />
                  {segmentsField}
                  <CitySelect
                    label="City"
                    placeholder="Search city…"
                    value={form.values.city}
                    onChange={(city) => form.setFieldValue("city", city)}
                    error={form.errors.city}
                  />
                  <NumberInput label="Years of experience" min={0} max={60} {...form.getInputProps("years_experience")} />
                  <Textarea label="Bio" placeholder="Tell companies about your recruiting experience" minRows={3} {...form.getInputProps("bio")} />
                  <Button type="submit" color="violet" loading={submitting}>Save changes</Button>
                </Stack>
              </form>
            ) : (
              <Stack gap="sm">
                {segmentsField}
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="City" value={profile?.city ?? "—"} disabled />
                  <TextInput label="Experience" value={formatExperience(profile?.years_experience ?? null)} disabled />
                </SimpleGrid>
                <Textarea label="Bio" value={profile?.bio ?? "—"} disabled minRows={3} />
                <Text size="sm" c="dimmed">Profile details are locked after KYC submission.</Text>
              </Stack>
            )}
          </ContentCard>
        </Tabs.Panel>

        <Tabs.Panel value="verification" pt="lg">
          <Stack gap="md">
            {!isIdentityVerified(user) && (
              <Alert color="orange">Verify email and phone before continuing through KYC.</Alert>
            )}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <EmailVerificationCard />
              <PhoneVerificationCard />
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="compliance" pt="lg">
          <Stack gap="md">
            <ContentCard>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>KYC documents</Title>
                  <Text size="sm" c="dimmed">{kycStatusLabel(profile?.kyc_status, docsComplete)}</Text>
                </div>
                <StatusBadge status={profile?.kyc_status ?? "pending"} />
              </Group>
              <Stack gap="sm">
                {DOC_SLOTS.map(({ key, label }) => (
                  <Group key={key} justify="space-between">
                    <Text size="sm">{label}</Text>
                    <Badge color={filled.has(key) ? (approved ? "green" : "blue") : "gray"} variant="light">
                      {docStatusLabel(filled, key, approved)}
                    </Badge>
                  </Group>
                ))}
                <Group justify="space-between">
                  <Text size="sm">Aadhaar</Text>
                  <Badge variant="light" color={kyc?.aadhaar_last4 ? "blue" : "gray"}>
                    {kyc?.aadhaar_last4 ? `XXXX-XXXX-${kyc.aadhaar_last4}` : "Not uploaded"}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">PAN</Text>
                  <Badge variant="light" color={kyc?.pan_set ? "blue" : "gray"}>{kyc?.pan_set ? "Uploaded" : "Not uploaded"}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Resume</Text>
                  <Group gap="sm">
                    <ResumeLink url={kyc?.resume_url ?? profile?.resume_url ?? null} variant="link" />
                    <Badge variant="light" color={kyc?.has_resume ? "blue" : "gray"}>{kyc?.has_resume ? "Uploaded" : "Missing"}</Badge>
                  </Group>
                </Group>
              </Stack>
            </ContentCard>
            <ContentCard>
              <Text size="sm" c="dimmed" mb="md">
                Accepted {termsVersion}{termsAcceptedAt ? ` on ${formatDate(termsAcceptedAt)}` : ""}
              </Text>
              <RecruiterTermsCard accepted onAcceptedChange={() => {}} version={termsVersion} />
            </ContentCard>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="preferences" pt="lg">
          <Stack gap="md">
            <NotificationPreferencesCard />
            <ContentCard>
              <Title order={4} mb="xs">Candidate intake link</Title>
              <Text size="sm" c="dimmed" mb="md">
                Share this link so candidates can join your talent pool and choose suitable roles.
              </Text>
              <ShareIntakeLinkButton existingLink={activeIntakeLink ?? null} />
            </ContentCard>
            <DeleteAccountCard />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
